import { useCallback, useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  readRecentProducts,
  rememberProduct,
  RECENT_PRODUCTS_CHANGED,
  type RecentProduct,
} from './recentProducts';

/** The signed-in user's entity ref, or undefined until identity resolves. */
export function useUserRef(): string | undefined {
  const identityApi = useApi(identityApiRef);
  const [userRef, setUserRef] = useState<string>();
  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (active) setUserRef(identity.userEntityRef);
      })
      .catch(() => {
        // No identity, no history: never fall back to a shared key.
      });
    return () => {
      active = false;
    };
  }, [identityApi]);
  return userRef;
}

/** The signed-in user's recently viewed products, kept in step with storage. */
export function useRecentProducts(): RecentProduct[] {
  const userRef = useUserRef();
  const [products, setProducts] = useState<RecentProduct[]>([]);
  useEffect(() => {
    if (!userRef) return undefined;
    const refresh = () => setProducts(readRecentProducts(userRef));
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener(RECENT_PRODUCTS_CHANGED, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(RECENT_PRODUCTS_CHANGED, refresh);
    };
  }, [userRef]);
  return products;
}

/**
 * Records a product view in the signed-in user's history. A no-op until
 * identity resolves.
 */
export function useRememberProduct(): (
  product: Omit<RecentProduct, 'viewedAt'>,
) => void {
  const userRef = useUserRef();
  return useCallback(
    product => {
      if (userRef) rememberProduct(userRef, product);
    },
    [userRef],
  );
}
